import type { BillingPeriod, PublicPlanTier } from './pricing';

export interface PendingUpgrade {
  tier: PublicPlanTier;
  billingPeriod: BillingPeriod;
}

/**
 * Start a Stripe Checkout session for the given tier.
 * Returns the checkout URL on success, or throws with a user-facing message.
 */
export async function startCheckout(
  targetTier: PublicPlanTier,
  billingPeriod: BillingPeriod = 'monthly',
): Promise<string> {
  const res = await fetch('/api/stripe/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan: targetTier, billingPeriod }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Checkout failed' }));
    throw new Error(body.error || `Checkout failed (${res.status})`);
  }

  const { url } = await res.json();
  if (!url) {
    throw new Error('No checkout URL returned');
  }

  return url;
}

/**
 * Persist upgrade intent in sessionStorage so it survives the OAuth round-trip.
 */
export function setPendingUpgrade(tier: PublicPlanTier, billingPeriod: BillingPeriod = 'monthly') {
  sessionStorage.setItem('pending_upgrade', JSON.stringify({ tier, billingPeriod }));
}

/**
 * Consume and clear any pending upgrade intent from sessionStorage.
 */
export function consumePendingUpgrade(): PendingUpgrade | null {
  const raw = sessionStorage.getItem('pending_upgrade');
  if (!raw) return null;
  sessionStorage.removeItem('pending_upgrade');

  try {
    const parsed = JSON.parse(raw) as Partial<PendingUpgrade>;
    if (
      (parsed.tier === 'student' || parsed.tier === 'power')
      && (parsed.billingPeriod === 'monthly' || parsed.billingPeriod === 'annual')
    ) {
      return parsed as PendingUpgrade;
    }
  } catch {
    // Ignore upgrade intents written by the retired Plus/Pro checkout flow.
  }
  return null;
}
